/** Блок «Юнифанс»: что форма отчёта шлёт при сдаче (ревью 04.11 ветки op-reports-unifans).
 *  У блока два писателя: форма (копит до «Отправить») и вкладка «Отчёты» (пишет сразу, любой оператор игры).
 *  Раньше форма слала СНИМОК — весь набор ачивок с открытия формы и все строки игроков (POST /players,
 *  DELETE + INSERT), и сдача молча стирала отмеченное во вкладке. Теперь форма помнит только то, что оператор
 *  нажал сам, показывает это поверх серверного, а при сдаче шлёт только нажатое: ачивки — achievements_delta
 *  в отчёте, теги и ★ — ops в POST /unifans-card/sync. Сервер (unifans-ops.ts) применяет их атомарно.
 *  Без api/client: веб-тесты гоняют файл голым node, его же берёт тест API (unifans-card.merge.test.ts). */
import type { UfRow } from "./unifansModel";

/** Ачивки, которые оператор нажал в форме: id → поставлена (true) или снята (false); последнее нажатие решает. */
export type AchTouch = Record<string, boolean>;

/** Что показать в пикере: набор с сервера + нажатое в форме. */
export function achShown(earned: readonly string[], touch: AchTouch): Set<string> {
  const s = new Set(earned);
  for (const [id, on] of Object.entries(touch)) { if (on) s.add(id); else s.delete(id); }
  return s;
}

/** Для отчёта: только нажатое; ничего не нажато — поля нет (напарник без пикера ничего не пишет). */
export function achDelta(touch: AchTouch): { on: string[]; off: string[] } | undefined {
  const e = Object.entries(touch);
  return e.length ? { on: e.filter(([, v]) => v).map(([k]) => k), off: e.filter(([, v]) => !v).map(([k]) => k) } : undefined;
}

/** Нажатие в строке игрока: тег или ★ — поставить (on) или снять. key — «r<id>»/«s<id>», telegram — запасной ключ. */
export interface UfOp { key: string; telegram: string; captain?: true; tag?: string; on: boolean }
type KRow = UfRow & { key?: string };

/** Нажатие по строке в её текущем виде: переключатель — on противоположен тому, что видно. */
export function ufOp(r: KRow, x: { captain?: true; tag?: string }): UfOp {
  return { key: r.key ?? "", telegram: r.telegram, ...x, on: x.captain ? !r.captain : !r.tags.includes(x.tag ?? "") };
}

const sameRow = (r: KRow, op: UfOp) => (!!op.key && r.key === op.key) || (!!op.telegram && r.telegram === op.telegram);

/** Строки с сервера + нажатия формы по порядку. ★ один на игру: включение снимает его у остальных. */
export function applyUfOps<T extends KRow>(rows: T[], ops: readonly UfOp[]): T[] {
  let out = rows;
  for (const op of ops) {
    const i = out.findIndex((r) => sameRow(r, op));
    if (i < 0) continue;
    out = out.map((x, j) => {
      if (op.captain) return { ...x, captain: j === i ? op.on : op.on ? false : x.captain };
      if (j !== i || !op.tag) return x;
      const has = x.tags.includes(op.tag);
      return { ...x, tags: op.on ? (has ? x.tags : [...x.tags, op.tag]) : x.tags.filter((t) => t !== op.tag) };
    });
  }
  return out;
}

/** Для отправки: по каждой паре (строка, тег/★) — последнее нажатие, порядок последних нажатий сохраняется. */
export function compactOps(ops: readonly UfOp[]): UfOp[] {
  const id = (o: UfOp) => `${o.key || `t:${o.telegram}`}|${o.captain ? "★" : `#${o.tag ?? ""}`}`;
  const last = new Map<string, number>();
  ops.forEach((o, i) => last.set(id(o), i));
  return ops.filter((o, i) => last.get(id(o)) === i);
}

/** Черновик формы до 04.11 хранил снимок (achSel, rPlayers): переводим в нажатия «поставить» — снятия не угадать. */
export function achTouchOfDraft(d: { achTouch?: AchTouch; achSel?: string[] }): AchTouch {
  return d.achTouch ?? Object.fromEntries((d.achSel ?? []).map((id) => [id, true]));
}
export function ufOpsOfDraft(d: { ufOps?: UfOp[]; rPlayers?: KRow[] }): UfOp[] {
  if (d.ufOps) return d.ufOps;
  return (d.rPlayers ?? []).filter((r) => r.telegram).flatMap((r): UfOp[] => [
    ...(r.captain ? [{ key: "", telegram: r.telegram, captain: true as const, on: true }] : []),
    ...r.tags.map((tag) => ({ key: "", telegram: r.telegram, tag, on: true })),
  ]);
}

/** Очередь записей вкладки: следующий запрос уходит после ответа на предыдущий (сбой очередь не рвёт). */
export function serialQueue() {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const p = tail.then(task, task);
    tail = p.catch(() => undefined);
    return p;
  };
}
